import { X } from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { t } from "@/i18n";
import { cn } from "@/lib/utils";

type Tone = "default" | "success" | "danger";
interface Toast { id: number; message: string; tone: Tone; group?: string }
interface Ctx { toast: (message: string, tone?: Tone, group?: string) => void; items: Toast[]; dismiss: (id: number) => void }
/** Long enough to read two lines; a tap dismisses earlier. */
const TOAST_MS = 3600;
const ToastCtx = createContext<Ctx | null>(null);

/** A toast with a group replaces the visible ones of that group, so "Preparing…" turns into its result instead of stacking. */
export const addToast = (items: readonly Toast[], next: Toast): Toast[] =>
  [...(next.group ? items.filter((i) => i.group !== next.group) : items), next];

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const seq = useRef(0);
  const toast = useCallback((message: string, tone: Tone = "default", group?: string) => {
    const id = ++seq.current;
    setItems((x) => addToast(x, { id, message, tone, group }));
    setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), TOAST_MS);
  }, []);
  const dismiss = useCallback((id: number) => setItems((x) => x.filter((i) => i.id !== id)), []);
  const value = useMemo(() => ({ toast, items, dismiss }), [toast, items, dismiss]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <ToastViewport />
    </ToastCtx.Provider>
  );
}

/** Native dialogs occupy the top layer: each sheet needs its own feedback viewport above that layer. */
export function ToastViewport() {
  const value = useContext(ToastCtx);
  if (!value) return null;
  const { items, dismiss } = value;
  return (
      <div role="status" aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-[calc(5.5rem_+_env(safe-area-inset-bottom))] z-[100] flex flex-col items-center gap-2 px-4 lg:bottom-6">
        {/* The message is text, not a button (a toast named "Saved" would answer queries for "Save"); the × dismisses. */}
        {items.map((i) => (
          <div
            key={i.id}
            className={cn(
              "toast-item pointer-events-auto flex w-fit max-w-full items-center gap-2 rounded-control py-2 pl-4 pr-2 text-sm font-medium shadow-lg backdrop-blur sm:max-w-md",
              i.tone === "success" && "bg-success text-on-pastel",
              i.tone === "danger" && "bg-danger text-on-pastel",
              i.tone === "default" && "bg-text text-bg",
            )}
          >
            <span className="min-w-0 break-words">{i.message}</span>
            <button type="button" aria-label={t("common.dismiss")} onClick={() => dismiss(i.id)} className="grid h-7 w-7 shrink-0 place-items-center rounded-full opacity-70 hover:bg-black/10 hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
  );
}

export function useToast() {
  const v = useContext(ToastCtx);
  if (!v) throw new Error("useToast outside ToastProvider");
  return v.toast;
}
